// AuditLog's query and CSV export (AuditLogModel).
import { adminApi, type AuditEntry } from './api';

export interface AuditFilter {
  actor: string | null;
  target: string | null;
  action: string | null;
  /** yyyy-MM-dd */
  from: string | null;
  to: string | null;
}

/** From: the day's first instant (UTC); To: its last (TimeOnly.MaxValue), as the API is asked. */
export const auditRange = (f: Pick<AuditFilter, 'from' | 'to'>) => ({
  from: f.from ? `${f.from}T00:00:00.0000000+00:00` : null,
  to: f.to ? `${f.to}T23:59:59.9999999+00:00` : null,
});

const csvField = (v: string) =>
  v.includes(',') || v.includes('"') || v.includes('\n') ? `"${v.replaceAll('"', '""')}"` : v;

/** "yyyy-MM-dd HH:mm:ss" in UTC */
const utc = (iso: string) => new Date(iso).toISOString().slice(0, 19).replace('T', ' ');

export function auditCsv(entries: readonly AuditEntry[]): string {
  const lines = ['When (UTC),Actor,Actor Email,IP Address,Action,Target Type,Target,Details'];
  for (const e of entries)
    lines.push(
      [
        utc(e.occurredAt),
        e.actorDisplayName,
        e.actorEmail,
        e.actorIpAddress ?? '',
        e.action,
        e.targetType,
        e.targetLabel,
        e.details ?? '',
      ]
        .map(csvField)
        .join(','),
    );
  return `${lines.join('\n')}\n`;
}

/** Every matching entry, page by page (the API serves up to 100 a page), up to `limit`. */
export async function allAuditEntries(f: AuditFilter, limit = 10_000): Promise<AuditEntry[]> {
  const all: AuditEntry[] = [];
  for (let page = 1; all.length < limit; page++) {
    const r = await adminApi.auditLog({ ...f, ...auditRange(f), page, pageSize: 100 });
    all.push(...r.items);
    if (r.items.length < 100 || all.length >= r.totalCount) break;
  }
  return all.slice(0, limit);
}
